import React, { Fragment, useEffect } from 'react';
import styled from 'styled-components';
import {
 Control, Controller, UseFormWatch, UseFormSetValue
} from 'react-hook-form';

import {
  Checkbox, FormControlLabel, FormGroup, TextField
} from '@mui/material';

import {
  CustomTooltip, FlexContainer, RequiredSelectHack, Text
} from 'components';

import { COUNTRY_CODE } from 'utils/constants';
import { translate } from 'utils/helpers';
import { Question } from '../Question/Question';

const CheckboxContainer = styled(FlexContainer).attrs({
  alignItems: 'center'
})`
  @media (min-width: 561px) {
    label {
      min-width: 440px;
    }
  }

  @media (max-width: 560px) {
    label {
      flex: 1
    }
  }
`;

const EquipementObservations = styled.textarea`
  min-height: 80px;
  max-width: 100%;
  min-width: 100%;
  padding: 1rem;
`;

interface FormStateCategoryView {
  assembly?: boolean;
  commissioning?: boolean;
  dismantling?: boolean;
  maintenance?: boolean;
  modification?: boolean;
  nonPeriodicControl?: boolean;
  observations?: string;
  other?: string;
  periodicControl?: boolean;
  // eslint-disable-next-line camelcase
  ui_other?: boolean;
}

export const InterventionType = ({
                                   control,
                                   countryCode,
                                   watch,
                                   setValue,
                                   interventionTypeValues
                                 }: {
  control: Control<FormStateCategoryView>;
  countryCode: string;
  interventionTypeValues: boolean[];
  setValue: UseFormSetValue<FormStateCategoryView>;
  watch: UseFormWatch<FormStateCategoryView>;
}) => {
  const isFrance = countryCode === COUNTRY_CODE.FR;
  const isCanada = countryCode === COUNTRY_CODE.CA;

  // Watch the needed values for the conditional logic
  const assembly = watch('assembly');
  const commissioning = watch('commissioning');
  const modification = watch('modification');
  const maintenance = watch('maintenance');
  const periodicControl = watch('periodicControl');
  const nonPeriodicControl = watch('nonPeriodicControl');
  const ui_other = watch('ui_other');
  const other = watch('other');

  const forceControl = isFrance && (
      assembly
      || commissioning
      || modification
      || maintenance
  );

  // Handle the conditional logic with useEffect
  useEffect(() => {
    // If a base type is selected, automatically add a periodic control if needed
    if (forceControl && !nonPeriodicControl && !periodicControl) {
      setValue('nonPeriodicControl', true);
    }
    // If "other" is not set, initialize it with empty string
    if (other == null) {
      setValue('other', '');
    }
  }, [forceControl, nonPeriodicControl, periodicControl, other, setValue]);

  return (
    <Fragment>
      <Question label={translate('interventionForm.interventionType.questionType')}>
        <RequiredSelectHack value={interventionTypeValues.some(type => type === true) ? 'valid' : ''} width="50%" />
        <FormGroup>
          {!isCanada && (
            <CheckboxContainer>
              <Controller
                      control={control}
                      name="assembly"
                      render={({ field }) => (
                        <FormControlLabel
                              control={<Checkbox {...field} checked={field.value || false} />}
                              label={translate('interventionTypes.assembly')}
                          />
                      )}
                  />
              <CustomTooltip text={translate('interventionForm.interventionType.assemblyTooltip')} />
            </CheckboxContainer>
            )}
          <CheckboxContainer>
            <Controller
                  control={control}
                  name="commissioning"
                  render={({ field }) => (
                    <FormControlLabel
                          control={<Checkbox {...field} checked={field.value || false} />}
                          label={translate('interventionTypes.commissioning')}
                      />
                  )}
              />
            <CustomTooltip text={translate('interventionForm.interventionType.commissioningTooltip')} />
          </CheckboxContainer>
          {!isCanada && (
            <CheckboxContainer>
              <Controller
                      control={control}
                      name="modification"
                      render={({ field }) => (
                        <FormControlLabel
                              control={<Checkbox {...field} checked={field.value || false} />}
                              label={translate('interventionTypes.modification')}
                          />
                      )}
                  />
              <CustomTooltip text={translate('interventionForm.interventionType.modificationTooltip')} />
            </CheckboxContainer>
            )}
          <CheckboxContainer>
            <Controller
                  control={control}
                  name="maintenance"
                  render={({ field }) => (
                    <FormControlLabel
                          control={<Checkbox {...field} checked={field.value || false} />}
                          label={translate('interventionTypes.maintenance')}
                      />
                  )}
              />
            <CustomTooltip text={translate('interventionForm.interventionType.maintenanceTooltip')} />
          </CheckboxContainer>
          <CheckboxContainer>
            <Controller
                  control={control}
                  name="periodicControl"
                  render={({ field }) => (
                    <FormControlLabel
                          control={
                            <Checkbox
                                {...field}
                                checked={field.value || false}
                                onChange={(e) => {
                                  // When deselecting a periodic control while a control is needed, select the non periodic control
                                  if (forceControl && periodicControl) {
                                    setValue('nonPeriodicControl', true);
                                  }
                                  // When selecting a periodic control, deselect the non periodic control
                                  if (!periodicControl) {
                                    setValue('nonPeriodicControl', false);
                                  }
                                  field.onChange(e);
                                }}
                            />
                          }
                          label={translate('interventionTypes.periodicControl')}
                      />
                  )}
              />
            <CustomTooltip text={translate('interventionForm.interventionType.periodicControlTooltip')} />
          </CheckboxContainer>
          <CheckboxContainer>
            <Fragment>
              <Controller
                    control={control}
                    name="nonPeriodicControl"
                    render={({ field }) => (
                      <FormControlLabel
                            control={
                              <Checkbox
                                  {...field}
                                  checked={field.value || false}
                                  onChange={(e) => {
                                    // When deselecting a non periodic control while a control is needed, select the periodic control
                                    if (forceControl && nonPeriodicControl) {
                                      setValue('periodicControl', true);
                                    }
                                    // When selecting a non periodic control, deselect the periodic control
                                    if (!nonPeriodicControl) {
                                      setValue('periodicControl', false);
                                    }
                                    field.onChange(e);
                                  }}
                              />
                            }
                            label={translate('interventionTypes.nonPeriodicControl')}
                        />
                    )}
                />
            </Fragment>
            <CustomTooltip text={translate('interventionForm.interventionType.nonPeriodicControlTooltip')} />
          </CheckboxContainer>
          {!isCanada && (
            <CheckboxContainer>
              <Controller
                      control={control}
                      name="dismantling"
                      render={({ field }) => (
                        <FormControlLabel
                              control={<Checkbox {...field} checked={field.value || false} />}
                              label={translate('interventionTypes.dismantling')}
                          />
                      )}
                  />
              <CustomTooltip text={translate('interventionForm.interventionType.dismantlingTooltip')} />
            </CheckboxContainer>
            )}
          <CheckboxContainer>
            <Controller
                  control={control}
                  name="ui_other"
                  render={({ field }) => (
                    <FormControlLabel
                          control={
                            <Checkbox
                                {...field}
                                checked={field.value || false}
                                onChange={(e) => {
                                  // When deselecting the 'other' type, remove the associated text
                                  if (ui_other) {
                                    setValue('other', '');
                                  }
                                  field.onChange(e);
                                }}
                            />
                          }
                          label={translate('interventionTypes.other')}
                      />
                  )}
              />
            <CustomTooltip text={translate('interventionForm.interventionType.otherTooltip')} />
          </CheckboxContainer>
          {ui_other && (
            <Controller
                    control={control}
                    name="other"
                    render={({ field }) => (
                      <TextField
                            {...field}
                            label={translate('interventionTypes.other')}
                            required
                        />
                    )}
                    rules={{ required: ui_other }}
                />
            )}
        </FormGroup>
      </Question>

      <Text margin="2rem 0 0.5rem 0">{`${translate('interventionForm.interventionType.observationType')} :`}</Text>
      <Controller
            control={control}
            name="observations"
            render={({ field }) => (
              <EquipementObservations
                    {...field}
                    placeholder={translate('interventionForm.interventionType.observationType')}
                />
            )}
        />
    </Fragment>
  );
};
